/**
 *
 * RoutesTable
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import sortBy from 'lodash/sortBy';
import { Button, Table } from 'react-bootstrap';
import withDialog from 'containers/Dialog/withDialog';
import Pagination from 'components/Pagination';
import Link from 'components/Link';
import Switch from 'components/Switch';
import Distance from 'components/Distance';
import formatDate from 'utils/formatDate';
import { PAGESIZE } from 'utils/constants';
import * as colors from 'styles/colors';
import messages from './messages';

const FIELDS = ['title', 'city', 'distance', 'public', 'modified'];

const Wrapper = styled(Table)`
  th svg {
    transform: translateX(5px);
    color: ${colors.grey500};
  }
  .td-index {
    text-align: right;
    width: 5%;
  }
  .td-title {
    width: 40%;
  }
  .td-city {
    width: 25%;
  }
  .td-distance {
    text-align: right;
    width: 5%;
    white-space: nowrap;
  }
  .td-public {
    text-align: center;
    width: 5%;
  }
  .td-modified {
    text-align: right;
    width: 15%;
    white-space: nowrap;
  }
  .td-remove {
    width: 5%;
    .btn {
      padding: 0 10px;
    }
  }
`;

/* eslint-disable react/prefer-stateless-function */
class RoutesTable extends React.PureComponent {
  state = {
    sort: 'modified',
    reversed: true,
    offset: 0,
    routes: this.props.routes,
  };

  handleChangePublic(id) {
    return isPublic => {
      const { onChange } = this.props;
      const body = { id, isPublic };
      onChange({ id, body });
    };
  }

  handleDelete(route) {
    return () => {
      const { onChange, dlgConfirm } = this.props;
      dlgConfirm({
        title: { ...messages.confirmDelete, values: { title: route.title } },
      })
        .then(() => {
          onChange({ id: route.id, method: 'DELETE' });
        })
        .catch(() => {});
    };
  }

  showField(route, field) {
    const value = route[field];
    switch (field) {
      case 'title':
        return <Link href={`/route/${route.id}`}>{value}</Link>;
      case 'distance':
        return <Distance distance={value} />;
      case 'public':
        return (
          <Switch
            id={`public_${route.id}`}
            value={value === 'Y'}
            onChange={this.handleChangePublic(route.id)}
            size="sm"
          />
        );
      case 'modified':
        return formatDate(value);
      default:
        return value;
    }
  }

  setSort = sort => () => {
    this.setState(prev => {
      if (prev.sort === sort) {
        return { reversed: !prev.reversed };
      }
      return { sort, reversed: sort === 'modified' };
    });
  };

  sortSliceRoutes() {
    const { sort, reversed, offset, routes } = this.state;
    const sorted = sortBy(routes, sort);
    if (reversed) {
      sorted.reverse();
    }
    const indexed = sorted.map((route, index) => ({ ...route, index }));
    return indexed.slice(offset, offset + PAGESIZE);
  }

  handlePaging = offset => {
    this.setState({ offset });
  };

  static getDerivedStateFromProps(props, state) {
    const { routes } = props;
    if (routes !== state.routes) {
      return { offset: 0, routes };
    }
    return null;
  }

  render() {
    const { offset, routes, sort, reversed } = this.state;
    const tableRoutes = this.sortSliceRoutes();
    const sortIcon = (
      <FontAwesomeIcon icon={reversed ? 'sort-down' : 'sort-up'} />
    );
    return (
      <React.Fragment>
        <Wrapper responsive hover>
          <thead>
            <tr>
              <th className="td-index">&nbsp;</th>
              {FIELDS.map(field => (
                <th
                  key={field}
                  className={`td-${field}`}
                  role="button"
                  tabIndex={0}
                  onClick={this.setSort(field)}
                >
                  <FormattedMessage {...messages[field]} />
                  {sort === field && sortIcon}
                </th>
              ))}
              <th className="td-remove">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {tableRoutes.map(route => (
              <tr key={route.id}>
                <td className="td-index">{route.index + 1}</td>
                {FIELDS.map(field => (
                  <td key={field} className={`td-${field}`}>
                    {this.showField(route, field)}
                  </td>
                ))}
                <td className="td-remove">
                  <Button bsStyle="link" onClick={this.handleDelete(route)}>
                    <FontAwesomeIcon icon="trash-alt" size="lg" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Wrapper>
        <div className="px-4">
          <Pagination
            value={offset}
            length={routes.length}
            onChange={this.handlePaging}
          />
        </div>
      </React.Fragment>
    );
  }
}

RoutesTable.propTypes = {
  onChange: PropTypes.func.isRequired,
  routes: PropTypes.array.isRequired,
  dlgConfirm: PropTypes.func.isRequired,
};

export default withDialog(RoutesTable);
